import React, { useEffect, useState } from "react";
import useDeviceType from "../../../custom-hooks/DeviceType";
import Button from "../../Molecules/button/Button";
import Input from "../../Atoms/Input/Input";
import ContactInput from "../../Atoms/Input/ContactInput";
import { EDIT_PROVIDER_PROFILE_DETAILS, TYPE_OF_GENDER } from "./constants";
import { VALIDATION_CONSTANTS } from "../../../utils/validation-constants/validationConstants";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import SelectDropdown from "../../Atoms/Select/Select";
import EventEHRImageAvatar from "../../Atoms/image-avatar/EventEHRImageAvatar";
import { getDepartmentList } from "../../../screens/provider-profile-screen/providerProfileSaga";
import { useDispatch, useSelector } from "react-redux";
import { componentKey } from "../../../screens/provider-profile-screen/providerProfileSlice";
import MultipleSelectWithEvents from "../../Atoms/Select/MultiSelect";
import { getLoginUserData } from "../../../utils/getToken";

interface PatientDetail {
  label: string;
  value: string;
}

interface EventEHRProviderProfileDetailCardWithEditProps {
  patientDetails?: PatientDetail[];
  defaultFieldsData?: any;
  onEditClick?: () => void; // Callback function
  handleOnSubmittedData?: (formData: any) => void;
  isEditable?: boolean;
}

const EventEHRProviderProfileDetailCardWithEdit = ({
  patientDetails,
  defaultFieldsData,
  onEditClick,
  handleOnSubmittedData,
  isEditable,
}: EventEHRProviderProfileDetailCardWithEditProps) => {
  const { departmentList } = useSelector((state: any) => state[componentKey]);
  const deviceType = useDeviceType();

  const myLogin: any = getLoginUserData();
  const Userdata = JSON.parse(myLogin);

  const [departmentListData, setDepartmentListData] = useState<any>([]);

  const dispatch = useDispatch();

  const deptId = defaultFieldsData?.departmentUsers?.length
    ? defaultFieldsData?.departmentUsers[0]?.department?.id
    : "";

  // GET DEPARTMENTS
  useEffect(() => {
    getDepartments();
  }, []);

  // GET DEPARTMENTS
  useEffect(() => {
    const departmentData = departmentList?.department?.map((data: any) => ({
      label: data?.name,
      value: data?.id,
    }));
    setDepartmentListData(departmentData);
  }, [departmentList]);

  // The saga pages through ALL departments (see getDepartmentListAsync), so the
  // dropdown is never capped by a fixed page limit.
  const getDepartments = () => {
    const payload = {
      facilityId: Userdata?.customAttributes?.facilityId,
    };
    dispatch(getDepartmentList(payload));
  };

  const fullName = `${defaultFieldsData?.firstName} ${defaultFieldsData?.lastName}`;

  const validationSchema = Yup.object().shape({
    [EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.FIRST_LAST_NAME,
        "First name can only contain characters"
      )
      .required("First name is required"),
    [EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.FIRST_LAST_NAME,
        "Last name can only contain characters"
      )
      .required("Last name is required"),
    [EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER]: Yup.string()
      .matches(VALIDATION_CONSTANTS.CONTACT_NUMBER, "Invalid contact number")
      .required("Contact number is required"),

    [EDIT_PROVIDER_PROFILE_DETAILS.GENDER]:
      Yup.string().required("Gender is required"),

    [EDIT_PROVIDER_PROFILE_DETAILS.DEPARTMENT]: Yup.array()
      .min(1, "At least one name must be selected")
      .required("Department is required"),
  });

  const commonButtonStyle = {
    width: "120px",
    height: "44px",
    padding: "12px 20px",
    fontSize: "16px",
  };

  const commonGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
    gap: "24px 18px",
  };

  // ADJUSTED GRID MEANS MADE SCREEN RESPONSIVE
  const adjustedGrid =
    deviceType === "tablet" || deviceType === "small-tablet"
      ? "1fr 1fr"
      : deviceType === "mobile"
        ? "1fr"
        : "1fr 1fr 1fr 1fr";
  const adjustedWidth =
    deviceType === "tablet" ||
    deviceType === "small-tablet" ||
    deviceType === "mobile";

  return (
    <div
      className="flex justify-between items-start background-color-[#E9E9E9] border-radius-8px"
      style={{ gap: "24px" }}
    >
      {/* padding: "20px 28px",  */}

      {deviceType != "mobile" && (
        <div
          className="border-radius-8px"
          style={{
            width: "140px",
            height: "140px",
          }}
        >
          <EventEHRImageAvatar
            labelText={fullName}
            image={defaultFieldsData?.userProfileImage}
            commonStyle={{
              // width: "60px",
              // height: "60px",
              padding: "25px",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              border: "2px solid #BCBCBC",
              borderRadius: "6px",
            }}
          />
        </div>
      )}
      {!isEditable ? (
        <div
          className="patient-details-card"
          style={{
            width: adjustedWidth ? "85%" : "100%",
            display: "grid",
            gridTemplateColumns: adjustedGrid,
            gap: "18px",
          }}
        >
          {patientDetails?.map((data, index) => (
            <div key={index} className="flex flex-col gap-[7px]">
              <div className="font-size-14px font-weight-500 color-727272">
                {data?.label}
              </div>
              <div className="color-393939 font-weight-500">{data?.value}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="w-full p-[6px]">
          <Formik
            initialValues={{
              [EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME]: `${defaultFieldsData?.firstName || "-"}`,
              [EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME]: `${defaultFieldsData?.lastName || "-"}`,
              [EDIT_PROVIDER_PROFILE_DETAILS.EMAIL]: `${defaultFieldsData?.email || "-"}`,
              [EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER]: `${defaultFieldsData?.contactNumber || "-"}`,
              [EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID]: `${defaultFieldsData?.bsaId || "-"}`,
              [EDIT_PROVIDER_PROFILE_DETAILS.BASE_CAMP]: `${defaultFieldsData?.campName || "-"}`,
              [EDIT_PROVIDER_PROFILE_DETAILS.GENDER]: `${defaultFieldsData?.gender || "-"}`,
              [EDIT_PROVIDER_PROFILE_DETAILS.ROLE]: `${defaultFieldsData?.roleNames || "-"}`,
              [EDIT_PROVIDER_PROFILE_DETAILS.PROVIDER_TYPE]: `${defaultFieldsData?.type || "-"}`,
              [EDIT_PROVIDER_PROFILE_DETAILS.SPECIALITY]: `${defaultFieldsData?.specialities || "-"}`,
              [EDIT_PROVIDER_PROFILE_DETAILS.DEPARTMENT]:
                defaultFieldsData?.departmentUsers?.map(
                  (data: any) => data?.department?.id
                ),
              [EDIT_PROVIDER_PROFILE_DETAILS.FACILITY_NAME]: `${defaultFieldsData?.facility?.name || "-"}`,
              [EDIT_PROVIDER_PROFILE_DETAILS.STATUS]: `${defaultFieldsData?.status || "-"}`,
            }}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={(values, { setSubmitting }) => {
              try {
                handleOnSubmittedData && handleOnSubmittedData(values);

                // Handle form data here
                // If needed, call an API or further process the values here
              } catch (error) {
                console.log(error);
              } finally {
                setSubmitting(false); // Reset submitting state
              }
            }}
          >
            {({
              errors,
              submitForm,
              isSubmitting,
              handleReset,
              isValid,
              values,
              touched,
              setFieldValue,
              handleBlur,
              handleSubmit,
            }: any) => {
              return (
                <Form
                  onSubmit={handleSubmit}
                  className="h-full flex flex-col justify-between rounded-lg"
                >
                  <div className="w-full flex flex-col rounded-lg">
                    {/* PATIENT DETAILS */}
                    <div>
                      <div style={commonGridStyle}>
                        <Input
                          textFieldLabel="First Name"
                          name={EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME}
                          value={
                            values[EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME]
                          }
                          onChangeCb={(e) => {
                            setFieldValue(
                              EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME,
                              e.target.value
                            );
                          }}
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME] &&
                            Boolean(
                              errors[EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME]
                            )
                          }
                          helperText={
                            touched[EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME]
                              ? errors[EDIT_PROVIDER_PROFILE_DETAILS.FIRST_NAME]
                              : undefined
                          }
                        />

                        <Input
                          disabled={isEditable}
                          textFieldLabel="Email"
                          name={EDIT_PROVIDER_PROFILE_DETAILS.EMAIL}
                          value={values[EDIT_PROVIDER_PROFILE_DETAILS.EMAIL]}
                          onChangeCb={(e) => {
                            setFieldValue(
                              EDIT_PROVIDER_PROFILE_DETAILS.EMAIL,
                              e.target.value
                            );
                          }}
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[EDIT_PROVIDER_PROFILE_DETAILS.EMAIL] &&
                            Boolean(errors[EDIT_PROVIDER_PROFILE_DETAILS.EMAIL])
                          }
                          helperText={
                            touched[EDIT_PROVIDER_PROFILE_DETAILS.EMAIL]
                              ? errors[EDIT_PROVIDER_PROFILE_DETAILS.EMAIL]
                              : undefined
                          }
                        />

                        <SelectDropdown
                          label="Gender"
                          value={values[EDIT_PROVIDER_PROFILE_DETAILS.GENDER]}
                          options={TYPE_OF_GENDER}
                          onChangeCb={(e) =>
                            setFieldValue(
                              EDIT_PROVIDER_PROFILE_DETAILS.GENDER,
                              e.target.value
                            )
                          }
                          onBlur={handleBlur}
                          error={
                            touched[EDIT_PROVIDER_PROFILE_DETAILS.GENDER] &&
                            Boolean(
                              errors[EDIT_PROVIDER_PROFILE_DETAILS.GENDER]
                            )
                          }
                          helperText={
                            touched[EDIT_PROVIDER_PROFILE_DETAILS.GENDER]
                              ? errors[EDIT_PROVIDER_PROFILE_DETAILS.GENDER]
                              : undefined
                          }
                        />
                        <Input
                          textFieldLabel="Last Name"
                          name={EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME}
                          value={
                            values[EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME]
                          }
                          onChangeCb={(e) => {
                            setFieldValue(
                              EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME,
                              e.target.value
                            );
                          }}
                          size="small"
                          onBlur={handleBlur}
                          error={
                            touched[EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME] &&
                            Boolean(
                              errors[EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME]
                            )
                          }
                          helperText={
                            touched[EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME]
                              ? errors[EDIT_PROVIDER_PROFILE_DETAILS.LAST_NAME]
                              : undefined
                          }
                        />

                        <ContactInput
                          textFieldLabel="Phone"
                          name={EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER}
                          value={
                            values[EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER]
                          }
                          onChangeCb={(value) => {
                            setFieldValue(
                              EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER,
                              value
                            );
                          }}
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[
                              EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER
                            ] &&
                            Boolean(
                              errors[
                                EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER
                              ]
                            )
                          }
                          helperText={
                            touched[
                              EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER
                            ]
                              ? errors[
                                  EDIT_PROVIDER_PROFILE_DETAILS.CONTACT_NUMBER
                                ]
                              : undefined
                          }
                          isContactNumber={true}
                        />

                        <Input
                          disabled={isEditable}
                          textFieldLabel="Provider Type"
                          name={EDIT_PROVIDER_PROFILE_DETAILS.PROVIDER_TYPE}
                          value={
                            values[EDIT_PROVIDER_PROFILE_DETAILS.PROVIDER_TYPE]
                          }
                          onChangeCb={(e) => {
                            setFieldValue(
                              EDIT_PROVIDER_PROFILE_DETAILS.PROVIDER_TYPE,
                              e.target.value
                            );
                          }}
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[
                              EDIT_PROVIDER_PROFILE_DETAILS.PROVIDER_TYPE
                            ] &&
                            Boolean(
                              errors[
                                EDIT_PROVIDER_PROFILE_DETAILS.PROVIDER_TYPE
                              ]
                            )
                          }
                          helperText={
                            touched[EDIT_PROVIDER_PROFILE_DETAILS.PROVIDER_TYPE]
                              ? errors[
                                  EDIT_PROVIDER_PROFILE_DETAILS.PROVIDER_TYPE
                                ]
                              : undefined
                          }
                        />

                        <Input
                          disabled={isEditable}
                          textFieldLabel="BSA ID"
                          name={EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID}
                          value={values[EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID]}
                          onChangeCb={(e) => {
                            setFieldValue(
                              EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID,
                              e.target.value
                            );
                          }}
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID] &&
                            Boolean(
                              errors[EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID]
                            )
                          }
                          helperText={
                            touched[EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID]
                              ? errors[EDIT_PROVIDER_PROFILE_DETAILS.BSA_ID]
                              : undefined
                          }
                        />
                        <MultipleSelectWithEvents
                          options={departmentListData}
                          defaultSelectedValues={
                            values[EDIT_PROVIDER_PROFILE_DETAILS?.DEPARTMENT]
                          }
                          label="Department"
                          name={EDIT_PROVIDER_PROFILE_DETAILS?.DEPARTMENT}
                          onItemsAdded={() => {}}
                          onItemsRemoved={() => {}}
                          onSelectionChanged={(selectedValues) => {
                            setFieldValue(
                              EDIT_PROVIDER_PROFILE_DETAILS.DEPARTMENT,
                              selectedValues
                            );
                          }}
                          onBlur={handleBlur}
                          error={
                            touched[
                              EDIT_PROVIDER_PROFILE_DETAILS?.DEPARTMENT
                            ] &&
                            Boolean(
                              errors[EDIT_PROVIDER_PROFILE_DETAILS?.DEPARTMENT]
                            )
                          }
                          helperText={
                            touched[EDIT_PROVIDER_PROFILE_DETAILS?.DEPARTMENT]
                              ? errors[
                                  EDIT_PROVIDER_PROFILE_DETAILS?.DEPARTMENT
                                ]
                              : undefined
                          }
                        />
                        <Input
                          disabled={isEditable}
                          textFieldLabel="Facility Name"
                          name={EDIT_PROVIDER_PROFILE_DETAILS.FACILITY_NAME}
                          value={
                            values[EDIT_PROVIDER_PROFILE_DETAILS.FACILITY_NAME]
                          }
                          onChangeCb={(e) => {
                            setFieldValue(
                              EDIT_PROVIDER_PROFILE_DETAILS.FACILITY_NAME,
                              e.target.value
                            );
                          }}
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[
                              EDIT_PROVIDER_PROFILE_DETAILS.FACILITY_NAME
                            ] &&
                            Boolean(
                              errors[
                                EDIT_PROVIDER_PROFILE_DETAILS.FACILITY_NAME
                              ]
                            )
                          }
                          helperText={
                            touched[EDIT_PROVIDER_PROFILE_DETAILS.FACILITY_NAME]
                              ? errors[
                                  EDIT_PROVIDER_PROFILE_DETAILS.FACILITY_NAME
                                ]
                              : undefined
                          }
                        />
                        <Input
                          disabled={isEditable}
                          textFieldLabel="Status"
                          name={EDIT_PROVIDER_PROFILE_DETAILS.STATUS}
                          value={values[EDIT_PROVIDER_PROFILE_DETAILS.STATUS]}
                          onChangeCb={(e) => {
                            setFieldValue(
                              EDIT_PROVIDER_PROFILE_DETAILS.STATUS,
                              e.target.value
                            );
                          }}
                          onBlur={handleBlur}
                          size="small"
                          error={
                            touched[EDIT_PROVIDER_PROFILE_DETAILS.STATUS] &&
                            Boolean(
                              errors[EDIT_PROVIDER_PROFILE_DETAILS.STATUS]
                            )
                          }
                          helperText={
                            touched[EDIT_PROVIDER_PROFILE_DETAILS.STATUS]
                              ? errors[EDIT_PROVIDER_PROFILE_DETAILS.STATUS]
                              : undefined
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-3 gap-4">
                    <Button
                      label={TEXT_CONSTANTS?.CANCEL}
                      variant="outlined"
                      onClickCb={() => {
                        onEditClick && onEditClick();
                      }}
                      style={{
                        ...commonButtonStyle,

                        backgroundColor: "white",
                        border: "1px solid #D2D2D2",
                        color: "#393939",
                      }}
                    />
                    <Button
                      type="submit"
                      label={TEXT_CONSTANTS?.SAVE}
                      variant="contained"
                      onClickCb={() => {}}
                      style={{
                        ...commonButtonStyle,
                      }}
                    ></Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default EventEHRProviderProfileDetailCardWithEdit;
